import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@phosphor-icons/react';

import { tierLabel } from '@/api-models/balance';
import { settingsBase, visibleNavGroups } from '@/features/settings/components/nav-config';
import { useAuth } from '@/contexts/auth-context';
import { teamPath, useSpace, useSpaceAccess } from '@/contexts/space-context';
import { TEAM_ROLE_LABEL, initials, tileColor } from '@/features/teams/components/teamVisuals';

/** Left rail for the settings shell: back button, the space being edited, grouped nav. */
export function SettingsSidebar() {
  const navigate = useNavigate();
  const { teamId } = useSpace();
  const { isPersonal, isAdmin } = useSpaceAccess();
  const base = settingsBase(teamId);
  const groups = visibleNavGroups({ isPersonal, isAdmin });

  return (
    <div className="flex w-62 shrink-0 flex-col gap-4 px-2.5 pb-3.5 pt-3">
      <button
        type="button"
        onClick={() => navigate(teamPath(teamId, '/sessions'))}
        className="flex h-9 items-center gap-2 rounded-lg px-2 text-left text-xs font-medium text-fg-secondary transition-colors hover:bg-black/5 hover:text-fg dark:hover:bg-white/5"
      >
        <ArrowLeftIcon size={16} className="text-fg-tertiary" />
        Back to app
      </button>

      <SpaceHeader />

      <nav className="flex flex-col gap-3.5">
        {groups.map((group) => (
          <div key={group.id} className="flex flex-col gap-0.5">
            <div className="flex h-5.5 items-center px-2">
              <span className="text-2xs font-semibold uppercase tracking-[0.06em] text-fg-tertiary">{group.label}</span>
            </div>
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.slug} to={`${base}/${item.slug}`} className="block no-underline">
                  {({ isActive }) => (
                    <div
                      className={`flex h-8 items-center gap-2.5 rounded-sm px-2 text-xs transition-colors ${
                        isActive
                          ? 'bg-surface-2 font-semibold text-fg'
                          : 'font-medium text-fg-secondary hover:bg-black/5 hover:text-fg dark:hover:bg-white/5'
                      }`}
                    >
                      <Icon
                        size={16}
                        weight={isActive ? 'fill' : 'regular'}
                        className={isActive ? 'text-fg' : 'text-fg-tertiary'}
                      />
                      {item.label}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );
}

/** Read-only reminder of which space these settings belong to — spaces are switched in the app, not here. */
function SpaceHeader() {
  const { me } = useAuth();
  const { activeTeam } = useSpace();
  if (!activeTeam) return null;

  const isPersonal = activeTeam.is_personal;
  const title = isPersonal ? me.name : activeTeam.name;
  const subtitle = isPersonal
    ? `Personal · ${tierLabel(activeTeam.license_tier)}`
    : [tierLabel(activeTeam.license_tier), TEAM_ROLE_LABEL[activeTeam.role]].filter(Boolean).join(' · ');

  return (
    <div className="flex h-10 items-center gap-2.25 px-1.5">
      {isPersonal ? (
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand">
          <span className="text-2xs font-semibold leading-none text-white">{initials(me.name)}</span>
        </div>
      ) : (
        <div
          className="flex size-7 shrink-0 items-center justify-center rounded-tile"
          style={{ backgroundColor: tileColor(activeTeam.id) }}
        >
          <span className="font-mono text-xs font-semibold leading-none text-white">
            {activeTeam.name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="truncate text-sm font-semibold leading-tight text-fg">{title}</span>
        <span className="truncate text-2xs font-medium leading-tight text-fg-tertiary">{subtitle}</span>
      </div>
    </div>
  );
}
